import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../shared/Toast';
import DeliveryZoneEditor from '../delivery/DeliveryZoneEditor';
import DriversSection from '../delivery/DriversSection';
import { DiscountSettings, DiscountSettingsSection, discountSectionId } from './DiscountPresetsField';
import { SavedNote, SwitchRow, useSavedFlash } from './instantSettings';
import { patchSettings } from './patchSettings';
import { SettingsSheet, SheetBlock, SheetSection } from './sheet';
import { StoreLoadState } from './storeSettingsForm';
import { API_BASE_URL } from '../config/api';

// Διανομείς, τρόποι παράδοσης (+ ζώνες) και εκπτώσεις. Οι υπόλοιποι διακόπτες καταργήθηκαν 12/10/2026
// με σταθερή συμπεριφορά (αναλυτικό κουπόνι, αυτόματη ολοκλήρωση και προφόρτωση POS, αλλαγή
// διανομέα πάντα· όχι QR, clock-in, GPS, «Έτοιμο», συμφωνία μετρητών) — βλ. routes/delivery.js.
// Διάταξη «Φύλλο» (13/10/2026): μία ενότητα το καθένα, κάθε αλλαγή αποθηκεύεται αμέσως.
type DeliverySettingsState = DiscountSettings;

type MethodField = 'delivery_self' | 'delivery_efood' | 'delivery_wolt';

const METHODS = 'delivery-methods';
const DISCOUNTS = discountSectionId('delivery');

const DeliverySettings: React.FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const token = localStorage.getItem('xentrix_token');
  const [settings, setSettings] = useState<DeliverySettingsState>({
    discount_presets: [5, 10, 15, 20],
    discount_allow_custom_percent: false,
    discount_allow_fixed_amount: false,
    discount_max_percent: 50,
    discount_max_amount: 100,
  });
  const [loading, setLoading] = useState(true);
  const [savedSection, flashSaved] = useSavedFlash();

  // Delivery method toggles
  const [methods, setMethods] = useState<Record<MethodField, boolean>>({
    delivery_self: true, delivery_efood: false, delivery_wolt: false,
  });
  const [storeLat, setStoreLat] = useState<number | null>(null);
  const [storeLng, setStoreLng] = useState<number | null>(null);
  const [savingMethods, setSavingMethods] = useState(false);

  useEffect(() => {
    if (store?.id) {
      fetchSettings();
      fetchStoreProfile();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const saveError = (status: number) => t(status === 403 ? 'settings:delivery.adminOnly' : 'settings:delivery.saveFailed');

  const fetchStoreProfile = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/stores/${store!.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const s = data.store || data;
        setMethods({
          delivery_self: s.delivery_self !== undefined ? s.delivery_self : true,
          delivery_efood: s.delivery_efood || false,
          delivery_wolt: s.delivery_wolt || false,
        });
        setStoreLat(s.latitude ? parseFloat(s.latitude) : null);
        setStoreLng(s.longitude ? parseFloat(s.longitude) : null);
      }
    } catch (err) {
      console.error('Failed to fetch store profile:', err);
    }
  };

  const saveDeliveryMethod = async (field: MethodField, value: boolean) => {
    setSavingMethods(true);
    // Ο διακόπτης αλλάζει μόνο αφού το δεχτεί ο server· σε αποτυχία μένει όπως ήταν και λέμε γιατί.
    const result = await patchSettings(`${API_BASE_URL}/api/stores/${store!.id}`, { [field]: value });
    if (result.ok) {
      setMethods(prev => ({ ...prev, [field]: value }));
      flashSaved(METHODS);
    } else {
      showToast('error', saveError(result.status));
    }
    setSavingMethods(false);
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/delivery/${store!.id}/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings({
          discount_presets: data.discount_presets || [5, 10, 15, 20],
          discount_allow_custom_percent: data.discount_allow_custom_percent || false,
          discount_allow_fixed_amount: data.discount_allow_fixed_amount || false,
          discount_max_percent: data.discount_max_percent ?? 50,
          discount_max_amount: data.discount_max_amount ?? 100,
        });
      }
    } catch (error) {
      console.error('Failed to fetch delivery settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async <K extends keyof DeliverySettingsState>(key: K, value: DeliverySettingsState[K]) => {
    const previous = settings[key];
    // Optimistic update — γυρίζει πίσω ΜΟΝΟ αυτό το πεδίο αν αποτύχει (πριν: έμενε η λάθος τιμή).
    setSettings(prev => ({ ...prev, [key]: value }));
    const result = await patchSettings<Partial<DeliverySettingsState>>(
      `${API_BASE_URL}/api/delivery/${store!.id}/settings`, { [key]: value }
    );
    if (result.ok) {
      setSettings(prev => ({ ...prev, [key]: result.data[key] ?? value }));
      flashSaved(DISCOUNTS);
    } else {
      setSettings(prev => ({ ...prev, [key]: previous }));
      showToast('error', saveError(result.status));
    }
  };

  return (
    <SettingsSheet eyebrow={t('settings:nav.groups.operations')} title={t('settings:delivery.title')} subtitle={t('settings:delivery.subtitle')}>
      {loading ? (
        <SheetBlock><StoreLoadState status="loading" onRetry={fetchSettings} /></SheetBlock>
      ) : (
        <>
          {/* Διανομείς (απόφαση ιδιοκτήτη 12/10/2026): εδώ και όχι στους Υπαλλήλους — δεν συνδέονται. */}
          {store?.id && <DriversSection storeId={store.id} />}

          <SheetSection id={METHODS} title={t('settings:delivery.methodsTitle')} hint={t('settings:delivery.methodsSubtitle')}
            aside={<SavedNote show={savedSection === METHODS} />}>
            <SwitchRow
              title={t('settings:delivery.selfDriversLabel')}
              subtitle={t('settings:delivery.selfDriversDesc')}
              checked={methods.delivery_self}
              disabled={savingMethods}
              onChange={(value) => saveDeliveryMethod('delivery_self', value)}
            />
            <SwitchRow
              title={t('settings:delivery.efoodDriversLabel')}
              subtitle={t('settings:delivery.efoodDriversDesc')}
              checked={methods.delivery_efood}
              disabled={savingMethods}
              onChange={(value) => saveDeliveryMethod('delivery_efood', value)}
            />
            <SwitchRow
              title={t('settings:delivery.woltDriversLabel')}
              subtitle={t('settings:delivery.woltDriversDesc')}
              checked={methods.delivery_wolt}
              disabled={savingMethods}
              onChange={(value) => saveDeliveryMethod('delivery_wolt', value)}
            />

            {/* Οι ζώνες αφορούν μόνο τους δικούς μας διανομείς: κάτω από τους διακόπτες, με τη λεπτή γραμμή
                ανάμεσα σε κομμάτια μιας ενότητας (όπως στις Εκπτώσεις). */}
            {methods.delivery_self && store?.id && (
              <div className="mt-5 pt-5 border-t border-line">
                <DeliveryZoneEditor
                  storeId={store.id}
                  storeLat={storeLat}
                  storeLng={storeLng}
                  googleMapsApiKey={store?.google_maps_api_key}
                />
              </div>
            )}
          </SheetSection>

          <DiscountSettingsSection
            idPrefix="delivery"
            settings={settings}
            onChange={(key, value) => updateSetting(key, value)}
            saved={savedSection === DISCOUNTS}
          />
        </>
      )}
    </SettingsSheet>
  );
};

export default DeliverySettings;
